import { useState, useEffect, useCallback, useRef } from "react";
import { authFetch } from "../App";
import { Search, Plus, Camera, PanelBottomClose, PanelBottomOpen } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  API, ALL_COL_KEYS, LAPTOP_DEFAULT_COLS, LAPTOP_BREAKPOINT,
  VISIBLE_COLS_KEY, DETAIL_COLLAPSED_KEY, LAST_CONTACT_KEY, SPLIT_HEIGHT_KEY,
  loadVisibleCols,
} from "../modules/crm/helpers";
import { ColumnVisibilityDropdown } from "../modules/crm/ColumnVisibilityDropdown";
import { CustomerTable } from "../modules/crm/CustomerTable";
import { CustomerDetail } from "../modules/crm/CustomerDetail";
import { CreateCustomerDialog } from "../modules/crm/CreateCustomerDialog";
import { ScanDialog } from "../modules/crm/ScanDialog";

export default function CrmPage() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showScan, setShowScan] = useState(false);
  const [scanData, setScanData] = useState(null);
  const [sortField, setSortField] = useState("firma");
  const [sortDir, setSortDir] = useState("asc");
  const [initialLoaded, setInitialLoaded] = useState(false);
  const [editOnLoad, setEditOnLoad] = useState(false);
  const [users, setUsers] = useState([]);
  const [filterVerantwortlicher, setFilterVerantwortlicher] = useState("");
  const [crmPerms, setCrmPerms] = useState(null);
  const [tableHeight, setTableHeight] = useState(() => {
    try { const v = localStorage.getItem(SPLIT_HEIGHT_KEY); return v ? Number(v) : null; } catch { return null; }
  });
  const splitRef = useRef(null);
  const containerRef = useRef(null);

  // ─── Column visibility ───
  const [visibleCols, setVisibleCols] = useState(() => {
    const saved = loadVisibleCols();
    if (saved) return saved;
    return window.innerWidth < LAPTOP_BREAKPOINT ? LAPTOP_DEFAULT_COLS : [...ALL_COL_KEYS];
  });
  const userSetCols = useRef(!!loadVisibleCols());

  // ─── Detail panel collapse ───
  const [detailCollapsed, setDetailCollapsed] = useState(() => {
    try { return localStorage.getItem(DETAIL_COLLAPSED_KEY) === "true"; } catch { return false; }
  });

  const toggleDetailCollapsed = useCallback(() => {
    setDetailCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem(DETAIL_COLLAPSED_KEY, String(next));
      return next;
    });
  }, []);

  // ─── Responsive listener ───
  useEffect(() => {
    const handleResize = () => {
      const laptop = window.innerWidth < LAPTOP_BREAKPOINT;
      if (!userSetCols.current) {
        setVisibleCols(laptop ? LAPTOP_DEFAULT_COLS : [...ALL_COL_KEYS]);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleVisibleColsChange = useCallback((cols) => {
    setVisibleCols(cols);
    userSetCols.current = true;
  }, []);

  const handleResetCols = useCallback(() => {
    const defaults = window.innerWidth < LAPTOP_BREAKPOINT ? LAPTOP_DEFAULT_COLS : [...ALL_COL_KEYS];
    setVisibleCols(defaults);
    localStorage.removeItem(VISIBLE_COLS_KEY);
    userSetCols.current = false;
  }, []);

  const loadContacts = useCallback(async () => {
    const params = search ? `?search=${encodeURIComponent(search)}` : "";
    const res = await authFetch(`${API}/crm/contacts${params}`);
    if (res.ok) setContacts(await res.json());
    setLoading(false);
  }, [search]);

  useEffect(() => { loadContacts(); }, [loadContacts]);

  // Load CRM permissions for the current user
  useEffect(() => {
    (async () => {
      try {
        const res = await authFetch(`${API}/crm/my-permissions`);
        if (res.ok) setCrmPerms(await res.json());
      } catch { /* silent */ }
    })();
  }, []);

  // Load users for Verantwortlicher dropdown (nur aktive mit CRM-Zugriff)
  useEffect(() => {
    (async () => {
      const res = await authFetch(`${API}/users`);
      if (res.ok) {
        const all = await res.json();
        setUsers(all.filter(u => u.is_active !== false && u.permissions?.can_access_crm));
      }
    })();
  }, []);

  // Auto-select last opened contact or first in list
  useEffect(() => {
    if (initialLoaded || loading || contacts.length === 0) return;
    const lastId = localStorage.getItem(LAST_CONTACT_KEY);
    if (lastId && contacts.some((c) => c.id === lastId)) {
      setSelectedId(lastId);
    } else {
      setSelectedId(contacts[0].id);
    }
    setInitialLoaded(true);
  }, [contacts, loading, initialLoaded]);

  const selectContact = useCallback((id) => {
    setSelectedId(id);
    if (id) localStorage.setItem(LAST_CONTACT_KEY, id);
  }, []);

  // Client-side sorting + filtering
  const filteredContacts = filterVerantwortlicher
    ? contacts.filter((c) => c.verantwortlicher === filterVerantwortlicher)
    : contacts;

  const sortedContacts = [...filteredContacts].sort((a, b) => {
    let valA, valB;
    if (sortField === "letzte_aktivitaet") {
      valA = typeof a.letzte_aktivitaet === "object" ? a.letzte_aktivitaet?.zeit || "" : a.letzte_aktivitaet || "";
      valB = typeof b.letzte_aktivitaet === "object" ? b.letzte_aktivitaet?.zeit || "" : b.letzte_aktivitaet || "";
    } else {
      valA = (a[sortField] || "").toString().toLowerCase();
      valB = (b[sortField] || "").toString().toLowerCase();
    }
    if (valA < valB) return sortDir === "asc" ? -1 : 1;
    if (valA > valB) return sortDir === "asc" ? 1 : -1;
    return 0;
  });

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  const handleScanned = (data) => {
    setScanData(data);
    setShowCreate(true);
  };

  const handleCreated = (newId) => {
    loadContacts();
    selectContact(newId);
  };

  const handleCloseDetail = () => {
    setSelectedId(null);
    setEditOnLoad(false);
  };

  const handleDblClick = (id) => {
    selectContact(id);
    setEditOnLoad(true);
  };

  // ─── Splitter drag logic ───
  const onSplitterMouseDown = useCallback((e) => {
    e.preventDefault();
    const startY = e.clientY;
    const startH = tableHeight || (containerRef.current ? containerRef.current.offsetHeight * 0.4 : 300);

    const onMouseMove = (ev) => {
      if (!containerRef.current) return;
      const containerH = containerRef.current.offsetHeight;
      const toolbarH = 38;
      const minTable = 100;
      const minDetail = 120;
      const maxTable = containerH - toolbarH - minDetail - 6;
      const diff = ev.clientY - startY;
      const newH = Math.min(maxTable, Math.max(minTable, startH + diff));
      setTableHeight(newH);
    };

    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      setTableHeight((h) => { localStorage.setItem(SPLIT_HEIGHT_KEY, String(h)); return h; });
    };

    document.body.style.cursor = "row-resize";
    document.body.style.userSelect = "none";
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  }, [tableHeight]);

  return (
    <div data-testid="crm-page" className="h-full flex flex-col overflow-hidden">
      <div ref={containerRef} className="flex-1 flex flex-col overflow-hidden min-h-0">
        {/* Toolbar */}
        <div className="px-3 py-1.5 border-b border-gray-200 bg-white flex items-center gap-2 flex-shrink-0" data-testid="crm-toolbar">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Suche nach Firma oder Telefonnummer..."
              className="pl-8 h-7 text-[11px]"
              data-testid="customer-search"
            />
          </div>
          <select
            value={filterVerantwortlicher}
            onChange={(e) => setFilterVerantwortlicher(e.target.value)}
            className="h-7 text-[11px] border border-gray-200 rounded px-2 bg-white text-gray-700 focus:outline-none focus:border-gray-400"
            data-testid="filter-verantwortlicher"
          >
            <option value="">Alle Verantwortlichen</option>
            {users.map((u) => (
              <option key={u.id} value={u.name}>{u.name}</option>
            ))}
          </select>
          <div className="flex gap-1.5 ml-auto items-center">
            <ColumnVisibilityDropdown visibleCols={visibleCols} onChange={handleVisibleColsChange} onReset={handleResetCols} />
            {(!crmPerms || crmPerms.can_edit_contacts) && (
              <>
                <Button variant="outline" size="sm" className="h-7 px-2.5 text-[10px]" onClick={() => setShowScan(true)} data-testid="scan-card-btn">
                  <Camera className="w-3 h-3 mr-1" /> Visitenkarte scannen
                </Button>
                <Button size="sm" className="h-7 px-2.5 text-[10px]" onClick={() => { setScanData(null); setShowCreate(true); }} data-testid="create-contact-btn">
                  <Plus className="w-3 h-3 mr-1" /> Neuer Kunde
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Table */}
        <div
          className={`overflow-auto transition-all duration-200 ${selectedId && !detailCollapsed ? "flex-shrink-0" : "flex-1"}`}
          style={selectedId && !detailCollapsed ? { height: tableHeight || "40%" } : undefined}
        >
          <CustomerTable
            contacts={sortedContacts}
            selectedId={selectedId}
            onSelect={selectContact}
            onDblClick={handleDblClick}
            sortField={sortField}
            sortDir={sortDir}
            onSort={handleSort}
            loading={loading}
            onRefreshList={loadContacts}
            users={users}
            visibleCols={visibleCols}
          />
        </div>

        {/* Splitter with collapse toggle */}
        {selectedId && (
          <div className="flex-shrink-0 relative" data-testid="split-area">
            {!detailCollapsed && (
              <div
                ref={splitRef}
                className="h-[3px] bg-gray-200 cursor-row-resize hover:bg-gray-400 active:bg-gray-500 transition-colors relative group"
                onMouseDown={onSplitterMouseDown}
                data-testid="split-handle"
              >
                <div className="absolute inset-x-0 -top-2 -bottom-2" />
                <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="w-8 h-[2px] rounded bg-gray-500/50" />
                </div>
              </div>
            )}
            <div className="h-6 bg-gray-50 border-y border-gray-200 flex items-center px-2.5 gap-2">
              <button
                onClick={toggleDetailCollapsed}
                className="flex items-center gap-1 text-[10px] text-gray-500 hover:text-gray-800 transition-colors"
                data-testid="toggle-detail-btn"
              >
                {detailCollapsed ? <PanelBottomOpen className="w-3.5 h-3.5" /> : <PanelBottomClose className="w-3.5 h-3.5" />}
                <span className="font-medium">{detailCollapsed ? "Details einblenden" : "Details ausblenden"}</span>
              </button>
              {detailCollapsed && selectedId && (
                <span className="text-[10px] text-gray-400 truncate ml-2">
                  {contacts.find((c) => c.id === selectedId)?.firma || "Kunde ausgewählt"}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Detail Panel */}
        {selectedId && !detailCollapsed && (
          <div className="flex-1 bg-white overflow-hidden min-h-0 transition-all duration-200" data-testid="detail-panel-container">
            <CustomerDetail
              contactId={selectedId}
              onClose={handleCloseDetail}
              onRefreshList={loadContacts}
              editOnLoad={editOnLoad}
              onEditConsumed={() => setEditOnLoad(false)}
            />
          </div>
        )}
      </div>

      {/* Dialogs */}
      <CreateCustomerDialog
        open={showCreate}
        onClose={() => { setShowCreate(false); setScanData(null); }}
        onSaved={handleCreated}
        initialData={scanData}
      />
      <ScanDialog open={showScan} onClose={() => setShowScan(false)} onScanned={handleScanned} />

      {/* Mobile FAB: Messe Scanner */}
      <a
        href="/app/scanner"
        className="fixed bottom-6 right-6 z-40 md:hidden w-14 h-14 bg-blue-600 text-white rounded-full shadow-lg flex items-center justify-center hover:bg-blue-700 active:scale-95 transition-all"
        data-testid="scanner-fab"
      >
        <Camera className="w-6 h-6" />
      </a>
    </div>
  );
}
